import React, { useState } from "react";
import DOMPurify from "dompurify";
import api, { describeAxiosError } from "@/lib/api";
import { X, PaperPlaneTilt, Eye, PencilSimple } from "@phosphor-icons/react";

// Pre-built outreach templates. Each carries a `subject`, a branded `eyebrow`
// tag + `headline` (rendered by the backend Unified Email Frame), an HTML
// `body`, and an optional `cta` (label + candidate-portal URL). `{{name}}`,
// `{{role}}`, `{{client}}` and `{{recruiter}}` are replaced before sending.
//
// Design language matches the "You're shortlisted" welcome drip email:
// small-caps eyebrow → bold headline → warm intro → structured "what's next"
// list → CTA. Keeps every candidate touchpoint feeling like it comes from
// one confident brand instead of a random recruiter inbox.
const CTA_PORTAL = "{{portal}}/candidate/dashboard";
const CTA_REFER = "{{portal}}/refer-and-earn";

const TEMPLATES = [
    {
        id: "blank",
        label: "Blank message",
        eyebrow: "Message from your recruiter",
        subject: "",
        headline: "",
        body: "",
        cta: null,
    },
    {
        id: "received",
        label: "Application received",
        eyebrow: "Application received",
        subject: "We got your application for {{role}} · NaukriDaddy",
        headline: "Your application is in — you're on the shortlist.",
        body:
            '<p style="margin:0 0 12px 0">Hi {{name}},</p>' +
            '<p style="margin:0 0 12px 0">Thanks for applying for the <strong>{{role}}</strong> role at <strong>{{client}}</strong>. ' +
            'Your profile just landed on my desk and I\'ve added it to the pipeline on NaukriDaddy.</p>' +
            '<div style="margin:16px 0;padding:14px 16px;border-left:3px solid #0A0A0A;background:#FAFAFA;font-size:13px;color:#0A0A0A">' +
            '<strong>What happens next</strong>' +
            '<ol style="margin:8px 0 0 0;padding-left:20px;line-height:1.7;color:#525252">' +
            '<li>Our AI matches your profile against the role brief (24h)</li>' +
            '<li>I share the top matches with the hiring team</li>' +
            '<li>If it\'s a fit, we set up a screening call this week</li>' +
            '</ol></div>' +
            '<p style="margin:16px 0 0 0;font-size:13px;color:#525252">Meanwhile, keep an eye on your inbox — I\'ll message you either way.</p>' +
            '<p style="margin:20px 0 0 0">Warmly,<br/><strong>{{recruiter}}</strong><br/><span style="color:#A3A3A3;font-size:12px">NaukriDaddy · Recruit smarter</span></p>',
        cta: { label: "Track your application", url: CTA_PORTAL },
    },
    {
        id: "interview",
        label: "Moving to interview",
        eyebrow: "You're moving forward",
        subject: "Next step: interview for {{role}} · NaukriDaddy",
        headline: "Good news — you're through to the interview round.",
        body:
            '<p style="margin:0 0 12px 0">Hi {{name}},</p>' +
            '<p style="margin:0 0 12px 0">Great news — the hiring team at <strong>{{client}}</strong> would like to move forward and interview you for the <strong>{{role}}</strong> role.</p>' +
            '<div style="margin:16px 0;padding:14px 16px;background:#EFF6FF;border:1px solid #DBEAFE;border-radius:0">' +
            '<p style="margin:0;font-size:11px;letter-spacing:0.08em;text-transform:uppercase;color:#1E3A8A;font-weight:800">Two things I need from you</p>' +
            '<ul style="margin:8px 0 0 0;padding-left:20px;line-height:1.7;color:#1E3A8A;font-size:13px">' +
            '<li><strong>Share 3 time slots</strong> that work for you in the next 5 business days</li>' +
            '<li><strong>Confirm your latest CV</strong> is up to date on your NaukriDaddy profile</li>' +
            '</ul></div>' +
            '<p style="margin:16px 0 0 0;font-size:13px;color:#525252">Reply to this email with the slots and I\'ll lock the calendar invite (Google Meet / Zoom / in-person — whatever works).</p>' +
            '<p style="margin:20px 0 0 0">Rooting for you,<br/><strong>{{recruiter}}</strong><br/><span style="color:#A3A3A3;font-size:12px">NaukriDaddy · Recruit smarter</span></p>',
        cta: { label: "Update your profile", url: CTA_PORTAL },
    },
    {
        id: "offer",
        label: "Offer letter intro",
        eyebrow: "You have an offer",
        subject: "Offer for {{role}} at {{client}} — please review",
        headline: "Congratulations — {{client}} would like to hire you.",
        body:
            '<p style="margin:0 0 12px 0">Hi {{name}},</p>' +
            '<p style="margin:0 0 12px 0">This is the email I love writing 🎉</p>' +
            '<p style="margin:0 0 12px 0"><strong>{{client}}</strong> has decided to extend an offer for the <strong>{{role}}</strong> role — the formal offer letter is attached to this email.</p>' +
            '<div style="margin:20px 0;padding:20px;background:linear-gradient(135deg,#0A0A0A 0%,#1E3A8A 100%);color:#FFFFFF;text-align:center">' +
            '<p style="margin:0;font-size:11px;letter-spacing:0.15em;text-transform:uppercase;color:#93C5FD;font-weight:800">Offer summary</p>' +
            '<p style="margin:8px 0 0 0;font-family:Georgia,serif;font-size:24px;font-weight:700;line-height:1.2">{{role}}</p>' +
            '<p style="margin:4px 0 0 0;font-size:14px;color:#DBEAFE">{{client}}</p>' +
            '</div>' +
            '<p style="margin:0 0 8px 0;font-size:11px;letter-spacing:0.08em;text-transform:uppercase;color:#0A0A0A;font-weight:800">Next steps</p>' +
            '<ol style="margin:0 0 12px 0;padding-left:20px;line-height:1.7;color:#525252;font-size:13px">' +
            '<li>Read the attached offer letter carefully</li>' +
            '<li>Reply with any questions — comp, joining date, relocation, ESOPs</li>' +
            '<li>Sign & send back within <strong style="color:#0A0A0A">5 business days</strong></li>' +
            '</ol>' +
            '<p style="margin:16px 0 0 0;font-size:13px;color:#525252">If you got another offer in parallel and need to negotiate, tell me — I\'d rather help you win the right role than lose you to silence.</p>' +
            '<p style="margin:20px 0 0 0">Cheers,<br/><strong>{{recruiter}}</strong><br/><span style="color:#A3A3A3;font-size:12px">NaukriDaddy · Recruit smarter</span></p>',
        cta: { label: "View offer in portal", url: CTA_PORTAL },
    },
    {
        id: "passed",
        label: "Polite rejection",
        eyebrow: "Update on your application",
        subject: "Update on your application for {{role}}",
        headline: "Not this one — but let's stay in touch.",
        body:
            '<p style="margin:0 0 12px 0">Hi {{name}},</p>' +
            '<p style="margin:0 0 12px 0">Thank you for the time and energy you put into interviewing for the <strong>{{role}}</strong> role at <strong>{{client}}</strong>.</p>' +
            '<p style="margin:0 0 12px 0">After a lot of back-and-forth, the team decided to move ahead with a different candidate this time. This wasn\'t an easy call — your background was genuinely strong, and I want to be upfront that it came down to a specific mix of experience the role needed on day one.</p>' +
            '<div style="margin:16px 0;padding:14px 16px;border-left:3px solid #059669;background:#ECFDF5;font-size:13px;color:#065F46">' +
            '<strong>What I\'d love you to do next</strong>' +
            '<ul style="margin:8px 0 0 0;padding-left:20px;line-height:1.7">' +
            '<li>Keep your NaukriDaddy profile live — I\'m already keeping an eye out for roles that fit you better</li>' +
            '<li>Refer a friend and earn <strong>₹5,000</strong> when they get hired through us</li>' +
            '<li>Reply to this email — happy to share honest feedback if you want it</li>' +
            '</ul></div>' +
            '<p style="margin:16px 0 0 0;font-size:13px;color:#525252">Wishing you the very best. Genuinely hope our paths cross again on a better-fit role soon.</p>' +
            '<p style="margin:20px 0 0 0">With respect,<br/><strong>{{recruiter}}</strong><br/><span style="color:#A3A3A3;font-size:12px">NaukriDaddy · Recruit smarter</span></p>',
        cta: { label: "Refer & earn ₹5,000", url: CTA_REFER },
    },
];

function fillTemplate(text, ctx) {
    return (text || "")
        .replaceAll("{{name}}", ctx.name || "there")
        .replaceAll("{{role}}", ctx.role || "the role")
        .replaceAll("{{client}}", ctx.client || "the hiring team")
        .replaceAll("{{recruiter}}", ctx.recruiter || "")
        .replaceAll("{{portal}}", ctx.portal || "");
}

export default function EmailCandidateModal({ candidate, jobTitle, clientName, recruiterName, onClose, onSent }) {
    const [templateId, setTemplateId] = useState("blank");
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [eyebrow, setEyebrow] = useState("Message from your recruiter");
    const [headline, setHeadline] = useState("");
    const [ctaLabel, setCtaLabel] = useState("");
    const [ctaUrl, setCtaUrl] = useState("");
    const [cc, setCc] = useState("");
    const [sending, setSending] = useState(false);
    const [err, setErr] = useState("");
    const [sent, setSent] = useState(null);
    // WYSIWYG preview toggle — flips between the compose form and a
    // rendered mock of what the candidate will actually receive.
    const [mode, setMode] = useState("compose"); // "compose" | "preview"
    // Which client's rendering to simulate in preview mode.
    const [previewClient, setPreviewClient] = useState("gmail"); // "gmail" | "outlook"

    const applyTemplate = (id) => {
        setTemplateId(id);
        const t = TEMPLATES.find((x) => x.id === id);
        if (!t) return;
        const portal = (typeof window !== "undefined" ? window.location.origin : "").replace(/\/$/, "");
        const ctx = {
            name: (candidate.name || "").split(" ")[0],
            role: jobTitle || "",
            client: clientName || "",
            recruiter: recruiterName || "",
            portal,
        };
        setSubject(fillTemplate(t.subject, ctx));
        setBody(fillTemplate(t.body, ctx));
        setEyebrow(t.eyebrow || "Message from your recruiter");
        setHeadline(fillTemplate(t.headline || "", ctx));
        setCtaLabel(t.cta?.label || "");
        setCtaUrl(fillTemplate(t.cta?.url || "", ctx));
    };

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setSending(true);
        try {
            const { data } = await api.post(`/candidates/${candidate.id}/email`, {
                subject,
                body_html: body,
                cc: cc.split(",").map((s) => s.trim()).filter(Boolean),
                eyebrow: eyebrow || undefined,
                headline: headline || undefined,
                cta_label: ctaLabel || undefined,
                cta_url: ctaUrl || undefined,
            });
            setSent(data);
            onSent?.();
        } catch (ex) {
            setErr(describeAxiosError(ex));
        } finally {
            setSending(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="email-candidate-modal"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-white border border-[#E5E5E5] w-full max-w-xl max-h-[90vh] overflow-y-auto"
            >
                <div className="flex items-center justify-between p-5 border-b border-[#E5E5E5] sticky top-0 bg-white">
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3]">
                            Outreach
                        </p>
                        <h2 className="font-heading text-2xl font-bold tracking-tight">
                            Email {candidate.name}
                        </h2>
                        <p className="text-xs text-[#525252] mt-1">{candidate.email}</p>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="close-email-modal"
                        className="p-2 hover:bg-[#F5F5F5]"
                    >
                        <X size={18} weight="bold" />
                    </button>
                </div>

                {sent ? (
                    <div className="p-6 space-y-4" data-testid="email-success">
                        <div className="border border-[#059669] bg-[#ECFDF5] text-[#065F46] p-4">
                            <p className="font-bold mb-1">Email sent ✓</p>
                            <p className="text-sm">From {sent.from_email} → {sent.to}</p>
                        </div>
                        <button
                            onClick={onClose}
                            className="bg-ink text-white px-5 py-2.5 text-xs font-bold uppercase tracking-wider"
                        >
                            Done
                        </button>
                    </div>
                ) : (
                    <>
                        {/* Compose ↔ Preview tab bar */}
                        <div className="flex gap-1 px-5 pt-4 border-b border-[#E5E5E5] -mb-px bg-white">
                            <button
                                type="button"
                                data-testid="email-tab-compose"
                                onClick={() => setMode("compose")}
                                className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors ${
                                    mode === "compose"
                                        ? "border-ink text-ink"
                                        : "border-transparent text-[#A3A3A3] hover:text-[#525252]"
                                }`}
                            >
                                <PencilSimple size={12} weight="bold" /> Compose
                            </button>
                            <button
                                type="button"
                                data-testid="email-tab-preview"
                                onClick={() => setMode("preview")}
                                className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors ${
                                    mode === "preview"
                                        ? "border-ink text-ink"
                                        : "border-transparent text-[#A3A3A3] hover:text-[#525252]"
                                }`}
                            >
                                <Eye size={12} weight="bold" /> Preview
                            </button>
                        </div>

                        {mode === "preview" && (
                            <div className="p-5 space-y-3" data-testid="email-preview">
                                {/* Client picker */}
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3]">
                                        Simulating
                                    </span>
                                    <div className="inline-flex bg-[#F5F5F5] border border-[#E5E5E5]" role="group">
                                        {[
                                            { key: "gmail", label: "Gmail" },
                                            { key: "outlook", label: "Outlook" },
                                            { key: "mobile", label: "Mobile" },
                                        ].map((c) => (
                                            <button
                                                key={c.key}
                                                type="button"
                                                data-testid={`email-client-${c.key}`}
                                                onClick={() => setPreviewClient(c.key)}
                                                className={`text-[10px] font-bold uppercase tracking-wider px-3 py-1 transition-colors ${
                                                    previewClient === c.key
                                                        ? "bg-ink text-white"
                                                        : "text-[#525252] hover:bg-white"
                                                }`}
                                            >
                                                {c.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="border border-[#E5E5E5] bg-[#FAFAFA] p-3 text-xs text-[#525252]">
                                    <p><span className="font-bold text-ink">To:</span> {candidate.email}</p>
                                    {cc && <p><span className="font-bold text-ink">Cc:</span> {cc}</p>}
                                    <p><span className="font-bold text-ink">Subject:</span> {subject || <em className="text-[#A3A3A3]">(no subject)</em>}</p>
                                </div>

                                {/* Rendered branded frame — client-specific styling */}
                                <div
                                    className="border border-[#E5E5E5] bg-white mx-auto"
                                    style={{
                                        fontFamily: previewClient === "outlook"
                                            ? "'Segoe UI', Calibri, Arial, sans-serif"
                                            : "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                                        maxWidth:
                                            previewClient === "mobile" ? 360 :
                                            previewClient === "outlook" ? 600 : 640,
                                    }}
                                >
                                    {/* Outlook-style external-sender warning */}
                                    {previewClient === "outlook" && (
                                        <div style={{ background: "#FFF4CE", borderBottom: "1px solid #F5C518", padding: "8px 24px", fontSize: 11, color: "#5C4A00" }}>
                                            <strong>You don&apos;t often get email from</strong> {candidate.email && candidate.email.includes("@") ? `no-reply@${candidate.email.split("@")[1] || "naukridaddy.com"}` : "no-reply@naukridaddy.com"}. <a href="#" style={{ color: "#0078D4" }}>Learn why this is important</a>
                                        </div>
                                    )}
                                    <div style={{ background: "#0A0A0A", padding: "20px 24px" }}>
                                        <p style={{ margin: 0, color: "#FFFFFF", fontFamily: previewClient === "outlook" ? "Georgia, 'Times New Roman', serif" : "Georgia, serif", fontSize: 20, fontWeight: 700, letterSpacing: "-0.02em" }}>
                                            naukri<span style={{ color: "#67E8F9" }}>daddy</span>
                                        </p>
                                    </div>
                                    <div style={{ padding: "24px 24px 8px" }}>
                                        {eyebrow && (
                                            <p style={{ margin: 0, fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase", color: "#A3A3A3", fontWeight: 800 }}>
                                                {eyebrow}
                                            </p>
                                        )}
                                        {headline && (
                                            <h1 style={{ margin: "8px 0 0", fontFamily: previewClient === "outlook" ? "Georgia, 'Times New Roman', serif" : "Georgia, serif", fontSize: 24, fontWeight: 700, color: "#0A0A0A", letterSpacing: "-0.01em", lineHeight: 1.25 }}>
                                                {headline}
                                            </h1>
                                        )}
                                    </div>
                                    <div style={{ padding: "8px 24px 20px", fontSize: 14, lineHeight: 1.6, color: "#0A0A0A" }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(body || '<p style="color:#A3A3A3;font-style:italic;margin:0">(body is empty — switch to Compose)</p>', { USE_PROFILES: { html: true } }) }} />
                                    {ctaLabel && ctaUrl && (
                                        <div style={{ padding: "0 24px 24px", textAlign: "left" }}>
                                            {previewClient === "outlook" ? (
                                                // Outlook renders table-based buttons for reliability
                                                <table role="presentation" cellPadding="0" cellSpacing="0" style={{ borderCollapse: "collapse" }}>
                                                    <tbody><tr><td style={{ padding: "12px 22px", background: "#0A0A0A", color: "#FFFFFF", fontSize: 12, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase" }}>{ctaLabel}</td></tr></tbody>
                                                </table>
                                            ) : (
                                                <span style={{ display: "inline-block", padding: "12px 22px", background: "#0A0A0A", color: "#FFFFFF", fontSize: 12, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", textDecoration: "none" }}>
                                                    {ctaLabel}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    <div style={{ background: "#FAFAFA", borderTop: "1px solid #E5E5E5", padding: "16px 24px" }}>
                                        <p style={{ margin: 0, fontSize: 11, color: "#A3A3A3", lineHeight: 1.5 }}>
                                            Sent from NaukriDaddy on behalf of {recruiterName || "your recruiter"}.<br />
                                            Reply to this email to reach {recruiterName || "them"} directly.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex justify-end gap-3 pt-1">
                                    <button
                                        type="button"
                                        onClick={() => setMode("compose")}
                                        className="border border-[#E5E5E5] hover:border-ink px-5 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors"
                                    >
                                        Back to edit
                                    </button>
                                    <button
                                        type="button"
                                        data-testid="email-submit-from-preview"
                                        onClick={submit}
                                        disabled={sending || !subject.trim() || !body.trim()}
                                        className="btn-gradient px-5 py-2.5 text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-40"
                                    >
                                        <PaperPlaneTilt size={14} weight="bold" />
                                        {sending ? "Sending…" : "Send email"}
                                    </button>
                                </div>
                            </div>
                        )}

                        {mode === "compose" && (
                    <form onSubmit={submit} className="p-5 space-y-4">
                        <div className="border border-[#E5E5E5] bg-[#FAFAFA] p-3">
                            <p className="text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1">
                                Sent via NaukriDaddy · Resend
                            </p>
                            <p className="text-xs text-[#525252] leading-relaxed">
                                Your message will be auto-wrapped in the NaukriDaddy branded email template
                                (logo header + footer) before it&apos;s delivered — you only need to write the body below.
                            </p>
                        </div>

                        <div>
                            <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1 block">
                                Template
                            </label>
                            <select
                                data-testid="email-template-select"
                                value={templateId}
                                onChange={(e) => applyTemplate(e.target.value)}
                                className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm focus:outline-none focus:border-cobalt"
                            >
                                {TEMPLATES.map((t) => (
                                    <option key={t.id} value={t.id}>
                                        {t.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1 block">
                                Subject
                            </label>
                            <input
                                type="text"
                                data-testid="email-subject-input"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                required
                                className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm focus:outline-none focus:border-cobalt"
                            />
                        </div>

                        <div>
                            <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1 block">
                                Message
                            </label>
                            <textarea
                                rows={10}
                                data-testid="email-body-input"
                                value={body}
                                onChange={(e) => setBody(e.target.value)}
                                required
                                placeholder="Type your message… HTML allowed (<b>, <br/>, <a href=…>)."
                                className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm focus:outline-none focus:border-cobalt font-mono"
                            />
                            <p className="mt-1 text-[10px] text-[#A3A3A3]">
                                Basic HTML supported. Use &lt;br/&gt; for line breaks.
                            </p>
                        </div>

                        <div>
                            <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1 block">
                                CC (comma-separated, optional)
                            </label>
                            <input
                                type="text"
                                value={cc}
                                onChange={(e) => setCc(e.target.value)}
                                placeholder="hiring.manager@example.com"
                                className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm focus:outline-none focus:border-cobalt"
                            />
                            <p className="mt-1 text-[10px] text-[#A3A3A3]">
                                The hiring manager tagged on this requirement is CC&apos;d automatically.
                            </p>
                        </div>

                        {err && (
                            <div
                                data-testid="email-error"
                                className="border border-[#DC2626] bg-[#FEF2F2] text-[#DC2626] text-sm px-3 py-2"
                            >
                                {err}
                            </div>
                        )}

                        <div className="flex gap-3 pt-1">
                            <button
                                type="button"
                                onClick={onClose}
                                className="border border-[#E5E5E5] hover:border-ink px-5 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                data-testid="email-submit-button"
                                disabled={sending}
                                className="btn-gradient flex-1 px-5 py-2.5 text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2"
                            >
                                <PaperPlaneTilt size={14} weight="bold" />
                                {sending ? "Sending…" : "Send email"}
                            </button>
                        </div>
                    </form>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
